import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import LayerRadar from "@/components/n360/LayerRadar";
import { GradeBadge, AccessBadge } from "@/components/n360/Badges";
import { ROLE_LABEL } from "@/lib/content";

function Stat({ icon, label, value, hint, onClick, testid }) {
  const Icon = Icons[icon] || Icons.Circle;
  return (
    <button onClick={onClick} data-testid={testid} className="n360-card p-5 text-left w-full">
      <div className="flex items-center justify-between mb-3">
        <span className="w-10 h-10 rounded-lg border border-[#F1551D]/25 grid place-items-center text-[#F1551D]"><Icon className="w-5 h-5" /></span>
        <Icons.ArrowUpRight className="w-4 h-4 text-[#aab9bd]" />
      </div>
      <div className="font-display text-3xl text-[#0D3C4C]">{value}</div>
      <div className="text-sm text-[#5f7d86]">{label}</div>
      {hint && <div className="text-[11px] text-[#F1551D]/80 mt-1">{hint}</div>}
    </button>
  );
}

export default function Dashboard() {
  const { user, meta } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/dashboard").then(({ data }) => setD(data)).catch(() => {}); }, []);

  const layers = meta?.layers || [];
  const isStaff = ["field_associate", "admin"].includes(user?.role);

  return (
    <div className="rise">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <div className="font-mono-i text-[11px] tracking-[0.3em] text-[#F1551D]/90 uppercase mb-2">Sovereign Member Portal</div>
          <h1 className="font-display text-3xl sm:text-4xl text-[#0D3C4C]">Welcome, {user?.name?.split(" ").slice(-1)[0] || "Principal"}.</h1>
          <p className="text-[#5f7d86] mt-1">{ROLE_LABEL[user?.role] || user?.role}</p>
        </div>
        <div className="flex items-center gap-2">
          <GradeBadge grade={user?.grade} />
          <AccessBadge level={user?.access_level} />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat testid="dash-completeness" icon="Gauge" label="Dossier Completeness" value={`${d?.completeness ?? 0}%`} hint="Complete your dossier" onClick={() => navigate("/app/profile")} />
        <Stat testid="dash-connections" icon="Users" label="Active Corridors" value={d?.connections ?? 0} hint={d?.incoming_requests ? `${d.incoming_requests} pending` : null} onClick={() => navigate("/app/directory")} />
        <Stat testid="dash-referral-points" icon="GitPullRequestArrow" label={`Contributor · ${d?.contributor_level?.name || "Connector"}`} value={d?.referral_points ?? 0} hint="Referral points" onClick={() => navigate("/app/referrals")} />
        <Stat testid="dash-network" icon="Globe" label="Network Members" value={d?.total_members ?? 0} hint="Browse directory" onClick={() => navigate("/app/directory")} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 n360-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-xl text-[#0D3C4C]">Your 8-Layer Intelligence Radar</h3>
            <button onClick={() => navigate("/app/profile")} className="text-xs text-[#F1551D] hover:underline" data-testid="dash-edit-layers">Calibrate →</button>
          </div>
          {d && Object.keys(d.layer_scores || {}).length > 0 ? (
            <LayerRadar layers={layers} scores={d.layer_scores} height={320} />
          ) : (
            <div className="text-center py-16 text-[#8598a0]">
              <Icons.Radar className="w-8 h-8 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No intelligence layers calibrated yet.</p>
              <button onClick={() => navigate("/app/profile")} className="mt-3 text-[#F1551D] text-sm hover:underline">Build your dossier →</button>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="n360-card p-6">
            <h3 className="font-display text-lg text-[#0D3C4C] mb-4">Quick Actions</h3>
            <div className="space-y-2">
              <button onClick={() => navigate("/app/profile")} data-testid="quick-profile" className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-[#F1551D]/15 hover:border-[#F1551D]/40 text-sm text-[#154859] transition-colors"><Icons.UserCircle className="w-4 h-4 text-[#F1551D]" /> Edit My Dossier</button>
              <button onClick={() => navigate("/app/directory")} data-testid="quick-directory" className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-[#F1551D]/15 hover:border-[#F1551D]/40 text-sm text-[#154859] transition-colors"><Icons.Search className="w-4 h-4 text-[#F1551D]" /> Explore Directory</button>
              <button onClick={() => navigate("/app/referrals")} data-testid="quick-referral" className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-[#F1551D]/15 hover:border-[#F1551D]/40 text-sm text-[#154859] transition-colors"><Icons.GitPullRequestArrow className="w-4 h-4 text-[#F1551D]" /> Submit a Referral</button>
              {isStaff && (
                <button onClick={() => navigate("/app/intelligence")} data-testid="quick-intelligence" className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-[#F1551D]/15 hover:border-[#F1551D]/40 text-sm text-[#154859] transition-colors"><Icons.ShieldQuestion className="w-4 h-4 text-[#F1551D]" /> Verification Queue {d?.pending_verifications ? `(${d.pending_verifications})` : ""}</button>
              )}
            </div>
          </div>

          <div className="n360-card p-6">
            <h3 className="font-display text-lg text-[#0D3C4C] mb-3">Clearance Status</h3>
            <p className="text-sm text-[#5f7d86] leading-relaxed">Your current clearance grants visibility up to <span className="text-[#B84214] font-medium">{user?.access_level}</span> intelligence. Higher tiers are granted by sovereign verification.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
